'use client';
import React, { useEffect, useState } from 'react';
import Link from 'next/link';
import { usePathname } from 'next/navigation';
import { Menu, X, LogIn } from 'lucide-react';
import AppLogo from '@/components/ui/AppLogo';
import { publicNav } from './publicData';
import { SocialIconLinks } from './SocialIcons';

interface PublicHeaderProps {
  minimal?: boolean;
}

export default function PublicHeader({ minimal = false }: PublicHeaderProps) {
  const pathname = usePathname();
  const [open, setOpen] = useState(false);

  // Force the menu closed on any route change and lock body scroll only while open.
  useEffect(() => {
    setOpen(false);
  }, [pathname]);
  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prev;
    };
  }, [open]);

  const isActive = (href: string) => (href === '/' ? pathname === '/' : pathname?.startsWith(href));

  if (minimal) {
    return (
      <header className="border-b border-border bg-background">
        <div className="mx-auto flex h-16 w-full max-w-screen-xl items-center px-4 md:px-6 lg:px-8">
          <Link href="/" className="flex items-center gap-2.5">
            <AppLogo size={38} />
            <span className="block text-sm font-bold tracking-tight text-foreground sm:text-base">Care Business School</span>
          </Link>
        </div>
      </header>
    );
  }

  return (
    <header className="sticky top-0 z-40 border-b border-border bg-background/90 backdrop-blur">
      <div className="mx-auto flex h-16 w-full max-w-screen-xl items-center justify-between gap-4 px-4 md:px-6 lg:px-8">
        <Link href="/" className="flex items-center gap-2.5" onClick={() => setOpen(false)}>
          <AppLogo size={38} priority />
          <span className="leading-tight">
            <span className="block text-sm font-bold tracking-tight text-foreground sm:text-base">Care Business School</span>
            <span className="hidden text-[10px] font-semibold uppercase tracking-[0.14em] text-accent sm:block">Education · Mentorship · Growth</span>
          </span>
        </Link>

        <nav className="hidden items-center gap-1 md:flex" aria-label="Main navigation">
          {publicNav.map((item) => (
            <Link
              key={item.key}
              href={item.href}
              aria-current={isActive(item.href) ? 'page' : undefined}
              className={`rounded-lg px-3.5 py-2 text-sm font-medium transition-colors duration-150 ${
                isActive(item.href) ? 'bg-primary/10 text-primary' : 'text-muted-foreground hover:bg-muted hover:text-foreground'
              }`}
            >
              {item.label}
            </Link>
          ))}
        </nav>

        <div className="flex items-center gap-2">
          <Link href="/login" className="btn-primary hidden !py-2 md:inline-flex">
            <LogIn size={16} /> Log in
          </Link>
          <div className="hidden md:ml-2 md:block">
            <SocialIconLinks variant="header" />
          </div>
          <button
            type="button"
            onClick={() => setOpen((v) => !v)}
            aria-label={open ? 'Close menu' : 'Open menu'}
            aria-expanded={open}
            style={{ touchAction: 'manipulation' }}
            className="btn-ghost flex h-11 w-11 items-center justify-center !p-2 md:hidden"
          >
            {open ? <X size={21} /> : <Menu size={21} />}
          </button>
        </div>
      </div>

      {open && (
        <div className="border-t border-border bg-background md:hidden">
          <nav className="mx-auto flex max-w-screen-xl flex-col gap-1 px-4 py-3" aria-label="Mobile navigation">
            {publicNav.map((item) => (
              <Link
                key={`m-${item.key}`}
                href={item.href}
                onClick={() => setOpen(false)}
                style={{ touchAction: 'manipulation' }}
                className={`flex min-h-[44px] items-center rounded-lg px-3 py-2.5 text-sm font-medium ${
                  isActive(item.href) ? 'bg-primary/10 text-primary' : 'text-foreground hover:bg-muted'
                }`}
              >
                {item.label}
              </Link>
            ))}
            <Link href="/login" onClick={() => setOpen(false)} style={{ touchAction: 'manipulation' }} className="btn-primary mt-2 min-h-[44px]">
              <LogIn size={16} /> Log in
            </Link>
          </nav>
        </div>
      )}
    </header>
  );
}
